import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import axios from "../../../helpers/axios";
import { generatePublicUrl } from "../../../urlConfig";
import useTrafficState from "../../../hooks/useTrafficState";
import SiteMediaViewer from "../../../components/media/SiteMediaViewer";
import {
    closeOfficeProductGallery,
} from "../../../actions/officeProductGallery.actions";

import "../../../scss/adminOffice/components/_global-entity-modal-AO.scss";

const isDirectUrl = (value) => (
    typeof value === "string" &&
    (
        value.startsWith("http://") ||
        value.startsWith("https://") ||
        value.startsWith("blob:") ||
        value.startsWith("data:")
    )
);

const resolveImageUrl = (value) => {
    const raw = String(value || "").trim();

    if (!raw) return "";
    if (isDirectUrl(raw)) return raw;
    if (raw.startsWith("/public/") || raw.startsWith("/media/")) return raw;

    return generatePublicUrl(raw);
};

const safeFileName = (value = "product") => (
    String(value || "product")
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 90) || "product"
);

export default function GlobalProductGalleryViewer() {
    const dispatch = useDispatch();
    const modal = useSelector((state) => state.officeProductGallery || {});
    const [trafficState, _toggleTrafficState, trafficMeta] = useTrafficState();

    const {
        open,
        officeId,
        productId,
        title,
        thumbnail,
        openedAt,
    } = modal;

    const requestedQuality = trafficMeta?.imageQuality === "high" && trafficState === false
        ? "high"
        : "low";

    const cacheRef = useRef(new Map());
    const [items, setItems] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [galleryMeta, setGalleryMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const close = useCallback(() => {
        dispatch(closeOfficeProductGallery());
    }, [dispatch]);

    useEffect(() => {
        if (!open || !officeId || !productId) return undefined;

        let mounted = true;
        const cacheKey = `${officeId}:${productId}:${requestedQuality}`;
        const cached = cacheRef.current.get(cacheKey);

        setActiveIndex(0);
        setError("");

        if (cached) {
            setItems(cached.items || []);
            setGalleryMeta(cached.meta || null);
            setLoading(false);
            return undefined;
        }

        setItems([]);
        setGalleryMeta(null);
        setLoading(true);

        axios
            .get(`/office/${officeId}/chat-entities/product/${productId}/pictures`, {
                params: {
                    imageQuality: requestedQuality,
                },
            })
            .then((response) => {
                if (!mounted) return;

                const nextItems = Array.isArray(response.data?.items)
                    ? response.data.items
                    : [];

                const nextMeta = {
                    imageQuality: response.data?.imageQuality || "low",
                    requestedQuality: response.data?.requestedQuality || requestedQuality,
                    highQualityAllowed: response.data?.highQualityAllowed === true,
                    title: response.data?.title || title || "Товар",
                };

                cacheRef.current.set(cacheKey, {
                    items: nextItems,
                    meta: nextMeta,
                });

                setItems(nextItems);
                setGalleryMeta(nextMeta);
            })
            .catch((requestError) => {
                if (!mounted) return;

                setError(
                    requestError?.response?.data?.error ||
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Не удалось загрузить фотографии товара."
                );
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [
        open,
        officeId,
        productId,
        requestedQuality,
        openedAt,
        title,
    ]);

    const viewerItems = useMemo(() => {
        if (items.length) return items;

        if (thumbnail && !loading && !error) {
            return [{
                _id: `thumb:${productId}`,
                img: thumbnail,
                title: title || "Товар",
                sourceQuality: "thumbnail",
            }];
        }

        return [];
    }, [items, thumbnail, loading, error, productId, title]);

    const getSource = useCallback((item) => (
        resolveImageUrl(item?.img || item?.url || item?.src || "")
    ), []);

    const qualityText = useMemo(() => {
        if (!galleryMeta) return "";

        if (galleryMeta.imageQuality === "high") {
            return "Высокое качество · оригинал";
        }

        if (
            galleryMeta.requestedQuality === "high" &&
            !galleryMeta.highQualityAllowed
        ) {
            return "Экономия трафика · 854 px · оригиналы недоступны по тарифу";
        }

        return "Экономия трафика · 854 px";
    }, [galleryMeta]);

    const handleDownload = useCallback(async (item, index) => {
        const source = getSource(item);
        if (!source) return;

        try {
            const response = await fetch(source, {
                credentials: "include",
            });

            if (!response.ok) throw new Error("download failed");

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = objectUrl;
            link.download = `${safeFileName(galleryMeta?.title || title)}-${index + 1}.${blob.type.includes("png") ? "png" : blob.type.includes("webp") ? "webp" : "jpg"}`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        } catch (_error) {
            window.open(source, "_blank", "noopener,noreferrer");
        }
    }, [galleryMeta?.title, getSource, title]);

    const handleShare = useCallback(async (item) => {
        const source = getSource(item);
        if (!source) return;

        const shareTitle = galleryMeta?.title || title || "Фото товара";

        try {
            if (navigator.share) {
                await navigator.share({
                    title: shareTitle,
                    url: source,
                });
                return;
            }

            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(source);
            }
        } catch (_error) {
            // Пользователь мог закрыть системное окно share — это не ошибка UI.
        }
    }, [galleryMeta?.title, getSource, title]);

    if (!open || typeof document === "undefined") return null;

    if (loading && !viewerItems.length) {
        return createPortal(
            <div className="product-gallery-global-AO" role="dialog" aria-modal="true">
                <button
                    type="button"
                    className="product-gallery-global-AO__backdrop"
                    onClick={close}
                    aria-label="Закрыть"
                />
                <div className="product-gallery-global-AO__state">
                    <span className="product-gallery-global-AO__spinner" />
                    <strong>Загружаем фотографии товара…</strong>
                    <small>
                        {requestedQuality === "high"
                            ? "Проверяем доступ и запрашиваем оригиналы"
                            : "Загружаем оптимизированные фото 854 px"}
                    </small>
                </div>
            </div>,
            document.body
        );
    }

    if (error && !viewerItems.length) {
        return createPortal(
            <div className="product-gallery-global-AO" role="dialog" aria-modal="true">
                <button
                    type="button"
                    className="product-gallery-global-AO__backdrop"
                    onClick={close}
                    aria-label="Закрыть"
                />
                <div className="product-gallery-global-AO__state is-error">
                    <strong>{error}</strong>
                    <button type="button" className="btn" onClick={close}>
                        Закрыть
                    </button>
                </div>
            </div>,
            document.body
        );
    }

    return (
        <SiteMediaViewer
            open
            items={viewerItems}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
            onClose={close}
            getSource={getSource}
            getItemMimeType={() => "image/jpeg"}
            getTitle={() => galleryMeta?.title || title || "Фото товара"}
            getSubtitle={() => qualityText}
            getAlt={() => galleryMeta?.title || title || "Фото товара"}
            onDownload={handleDownload}
            onShare={handleShare}
            downloadLabel="Скачать"
            shareLabel="Поделиться"
            emptyText="У товара нет доступных фотографий"
            loadingText="Загружаем фотографию…"
        />
    );
}
